import { useState, useEffect } from "react";
import { View } from ".";
import { clsx } from "clsx";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
}

interface TooltipPosition {
  top: number;
  left: number;
  transform: string;
}

export const Tooltip = ({ content, children, placement = "top", className }: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ top: 0, left: 0, transform: "translate(-50%, -100%)" });
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (triggerElement && isVisible) {
      const rect = triggerElement.getBoundingClientRect();
      const viewportPadding = 12;
      const tooltipWidth = 260; // keep in sync with CSS width below
      const positions = {
        top: {
          top: Math.max(viewportPadding, rect.top - 10),
          left: Math.min(window.innerWidth - viewportPadding - tooltipWidth, Math.max(viewportPadding, rect.left + rect.width / 2 - tooltipWidth / 2 - 20)),
          transform: "translate(0, -100%)",
        },
        bottom: {
          top: Math.min(window.innerHeight - viewportPadding, rect.bottom + 10),
          left: Math.min(window.innerWidth - viewportPadding - tooltipWidth, Math.max(viewportPadding, rect.left + rect.width / 2 - tooltipWidth / 2 - 20)),
          transform: "translate(0, 0)",
        },
        left: {
          top: Math.min(window.innerHeight - viewportPadding, Math.max(viewportPadding, rect.top + rect.height / 2)),
          left: Math.max(viewportPadding, rect.left - 10),
          transform: "translate(-100%, -50%)",
        },
        right: {
          top: Math.min(window.innerHeight - viewportPadding, Math.max(viewportPadding, rect.top + rect.height / 2)),
          left: Math.min(window.innerWidth - viewportPadding, rect.right + 10),
          transform: "translate(0, -50%)",
        },
      };

      setTooltipPosition(positions[placement]);
    }
  }, [isVisible, placement, triggerElement]);

  return (
    <>
      <View
        ref={setTriggerElement}
        className={clsx("relative inline-block", className)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        {children}
      </View>
      {isVisible &&
        createPortal(
          <View
            className={clsx(
              "fixed px-3 py-2 text-sm rounded-lg shadow-lg",
              "bg-white dark:bg-gray-800",
              "text-gray-900 dark:text-gray-100",
              "border border-gray-200 dark:border-gray-600",
              "backdrop-blur-sm",
              "whitespace-pre-line",
              // Match width used by position clamping
              "w-[265px]",
              "transition-all duration-200",
              "z-[9999]",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none",
            )}
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: tooltipPosition.transform,
            }}
          >
            {content}
          </View>,
          document.body,
        )}
    </>
  );
};
